import { getAuthToken } from '../utils/cookies.js';
import { API_BASE_URL, TRAINING_URL } from './constants.js';
import { getOrganizationUnique } from './utils.js';

const REGISTRATION_INCOMPLETE_CODE = 'registration_incomplete';

// Same literal fallback authSession.js uses for the current-tab 409 case — this one
// is for the reserved Training tab, which never runs through authSession.js.
const POSTREGISTRATION_URL = '/users/postregistration/';

const showReservedTabError = (reservedTab, message) => {
	if (!reservedTab || reservedTab.closed) return;
	try {
		const { document: reservedDoc } = reservedTab;
		reservedDoc.title = 'Training';
		const body = reservedDoc.body;
		body.style.font = '14px sans-serif';
		body.style.padding = '2rem';
		body.textContent = message;
	} catch {
		// The tab may already be mid-navigation or closed by the user — nothing left to do.
	}
};

/**
 * Mint-and-launch for the "Training" entry points: opens the destination tab
 * SYNCHRONOUSLY (transient user activation) before the mint fetch runs, because a
 * fetch-then-open sequence is popup-blocked in Chrome/Safari/Firefox. `w.opener = null`
 * severs the handle to a cross-origin page (reverse tabnabbing) while keeping the local
 * reference this function needs to navigate the tab once mint resolves.
 */
export const launchTraining = async () => {
	const reservedTab = window.open('', '_blank');
	if (reservedTab) reservedTab.opener = null;

	const organizationUnique = getOrganizationUnique();
	if (!organizationUnique) {
		showReservedTabError(reservedTab, 'No organization selected.');
		return;
	}

	let response;
	try {
		const token = getAuthToken();
		response = await fetch(`${API_BASE_URL}/auth/sso/mint`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				...(token && { token })
			},
			body: JSON.stringify({ organizationUnique })
		});
	} catch {
		showReservedTabError(reservedTab, 'Could not reach Training. Please try again.');
		return;
	}

	// An error body isn't guaranteed to be JSON (an nginx/proxy 4xx is HTML).
	let data = null;
	try {
		data = await response.json();
	} catch {
		data = null;
	}

	// RequestInspector rejects a registration-incomplete account before the mint
	// controller ever runs — send the reserved tab to finish registration instead of
	// leaving it on a dead about:blank.
	if (response.status === 409 && data?.data?.code === REGISTRATION_INCOMPLETE_CODE) {
		if (reservedTab) reservedTab.location = data.data.nextPath || POSTREGISTRATION_URL;
		return;
	}

	const nonce = data?.data?.nonce;
	if (!response.ok || !nonce) {
		showReservedTabError(reservedTab, 'Could not open Training. Please try again.');
		return;
	}

	if (reservedTab) reservedTab.location = `${TRAINING_URL}/sso?n=${encodeURIComponent(nonce)}`;
};
